import React, { useState, useEffect } from "react";
import {
  Users, Plus, Search, Pencil, Trash2, X,
  Eye, EyeOff, ShieldCheck, UserCircle2, Mail, Lock, ChevronDown, Phone, Settings,
} from "lucide-react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useAPI } from "../Context/MyContext";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

// ─── PhoneInput styles ────────────────────────────────────────────────────────
const PHONE_STYLES = `
  .PhoneInput {
    display: flex; align-items: center;
    width: 100%; border: 1px solid #e2e8f0; border-radius: 0.75rem;
    background: #f8fafc; overflow: hidden;
    transition: box-shadow .15s, border-color .15s;
  }
  .PhoneInput:focus-within {
    border-color: #6ee7b7; box-shadow: 0 0 0 3px rgba(110,231,183,.2); background:#fff;
  }
  .PhoneInputCountry {
    display: flex; align-items: center; padding: 0 10px 0 12px;
    border-right: 1px solid #e2e8f0; background: #f1f5f9;
    height: 42px; gap: 6px; cursor: pointer; flex-shrink: 0; position: relative;
  }
  .PhoneInputCountryIcon { width:22px; height:16px; border-radius:2px; overflow:hidden; box-shadow:0 0 0 1px rgba(0,0,0,.08); }
  .PhoneInputCountrySelect { opacity:0; position:absolute; inset:0; cursor:pointer; width:100%; height:100%; }
  .PhoneInputCountrySelectArrow { width:7px; height:7px; border-right:1.5px solid #94a3b8; border-bottom:1.5px solid #94a3b8; transform:rotate(45deg) translateY(-2px); }
  .PhoneInputInput { flex:1; border:none; background:transparent; padding:0 12px; height:42px; font-size:.875rem; color:#0f172a; outline:none; min-width:0; }
  .PhoneInputInput::placeholder { color:#94a3b8; }
`;

// ─── Constants ────────────────────────────────────────────────────────────────
const ROLES = ["Agent"];
const QUOTA = 10;


const PERMISSIONS_LIST = [
  // { key: "viewChats", label: "View Chats" },
  { key: "replyChats", label: "Reply to Chats" },
  { key: "marketingTemplate", label: "Access Marketing Templates" },
  { key: "viewCustomerName", label: "View Customer Name" },
  { key: "viewWhatsappNumber", label: "View WhatsApp Number" },
  // manage the calls
  { key: "manageCalls", label: "Manage Calls" },
];

const FEATURES_LIST = [
  { key: "contacts", label: "Contacts" },
  { key: "broadcasting", label: "Broadcast" },
  { key: "liveChat", label: "Live Chat" },
  { key: "workflows", label: "Workflows" },
];

const AVATAR_COLORS = [
  "from-violet-500 to-purple-600",
  "from-emerald-500 to-teal-600",
  "from-sky-500 to-blue-600",
  "from-rose-500 to-pink-600",
  "from-amber-500 to-orange-600",
  "from-indigo-500 to-violet-600",
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

const avatarColor = (name = "") =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];

const inputCls =
  "w-full text-sm border border-slate-200 rounded-xl px-3 py-[10px] bg-slate-50 " +
  "focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:bg-white " +
  "placeholder-slate-400 transition-all";

// ─── Badges ───────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    Online: "bg-emerald-100 text-emerald-700",
    Offline: "bg-slate-100 text-slate-500",
    Busy: "bg-amber-100 text-amber-700",
  };
  return (
    <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${map[status] || map.Offline}`}>
      {status}
    </span>
  );
};

const RolePill = ({ role }) => {
  const map = {
    Owner: "bg-violet-100 text-violet-700",
    Admin: "bg-blue-100 text-blue-700",
    Supervisor: "bg-teal-100 text-teal-700",
    Agent: "bg-slate-100 text-slate-600",
  };
  return (
    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${map[role] || map.Agent}`}>
      {role}
    </span>
  );
};

// ─── Toggle switch ────────────────────────────────────────────────────────────
const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none flex-shrink-0 ${checked ? "bg-blue-500" : "bg-slate-200"
      }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? "translate-x-6" : "translate-x-1"
        }`}
    />
  </button>
);

// ─── Form field wrapper ───────────────────────────────────────────────────────
const FormField = ({ icon: Icon, label, children }) => (
  <div>
    <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
      <Icon size={12} className="text-slate-400" />
      {label}
    </label>
    {children}
  </div>
);

// ─── Modal shell ──────────────────────────────────────────────────────────────
const Modal = ({ open, onClose, title, children }) => {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200"
        style={{ animation: "modalIn .2s cubic-bezier(.22,.68,0,1.2)" }}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900 truncate pr-4">{title}</h2>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0"
          >
            <X size={15} />
          </button>
        </div>
        <div className="px-6 pb-6 pt-4 max-h-[82vh] overflow-y-auto">
          {children}
        </div>
      </div>
      <style>{`@keyframes modalIn{from{opacity:0;transform:scale(.95) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}`}</style>
    </div>
  );
};

// ─── Agent Form ───────────────────────────────────────────────────────────────
const AgentForm = ({ initial = {}, onSubmit, onCancel, submitLabel = "Create Agent", loading = false }) => {
  const [name, setName] = useState(initial.name || "");
  const [email, setEmail] = useState(initial.email || "");
  const [phone, setPhone] = useState(initial.phone || "");
  const [role, setRole] = useState(ROLES.includes(initial.role) ? initial.role : "Agent");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [pwError, setPwError] = useState("");
  const isEdit = !!initial.id;

  const handleSubmit = (e) => {
    e.preventDefault();
    setPwError("");

    if (!isEdit || password) {
      if (password !== confirmPassword) {
        setPwError("Passwords do not match");
        return;
      }
      if (!isEdit && password.length < 6) {
        setPwError("Password must be at least 6 characters");
        return;
      }
    }

    const payload = { name, email, phone, role };
    if (password) payload.password = password;
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3.5">
      <style>{PHONE_STYLES}</style>

      <FormField icon={UserCircle2} label="Agent's Name">
        <input required value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Full name" className={inputCls} />
      </FormField>

      <FormField icon={Mail} label="Agent's Email">
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="agent@company.com" className={inputCls} />
      </FormField>

      <FormField icon={Phone} label="WhatsApp Number">
        <PhoneInput
          international defaultCountry="PK"
          value={phone} onChange={setPhone}
          placeholder="Enter phone number"
        />
      </FormField>

      <FormField icon={ShieldCheck} label="Role">
        <div className="relative">
          <select value={role} onChange={(e) => setRole(e.target.value)}
            className={`${inputCls} appearance-none pr-8 cursor-pointer`}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </FormField>

      {/* Login Details divider */}
      <div className="flex items-center gap-3 py-1">
        <div className="flex-1 h-px bg-slate-100" />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Login Details</span>
        <div className="flex-1 h-px bg-slate-100" />
      </div>

      <FormField icon={Lock} label={isEdit ? "New Password (leave blank to keep)" : "Password"}>
        <div className="relative">
          <input
            required={!isEdit}
            type={showPw ? "text" : "password"}
            value={password}
            onChange={(e) => { setPassword(e.target.value); setPwError(""); }}
            placeholder={isEdit ? "Leave blank to keep current" : "Set a password"}
            className={`${inputCls} pr-10`}
          />
          <button type="button" onClick={() => setShowPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
            {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </FormField>

      <FormField icon={Lock} label="Confirm Password">
        <div className="relative">
          <input
            required={!isEdit}
            type={showConfirmPw ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setPwError(""); }}
            placeholder="Re-enter password"
            className={`${inputCls} pr-10 ${pwError ? "border-red-300 focus:ring-red-200" : ""}`}
          />
          <button type="button" onClick={() => setShowConfirmPw((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
            {showConfirmPw ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
        {pwError && (
          <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-red-500 inline-block" />
            {pwError}
          </p>
        )}
      </FormField>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={loading}
          className="px-5 py-2 text-sm font-semibold text-white bg-[#0d3d36] hover:bg-[#0a2f29] disabled:opacity-60 rounded-xl transition-colors shadow-sm">
          {loading ? "Saving..." : submitLabel}
        </button>
      </div>
    </form>
  );
};

// ─── Delete Confirm ───────────────────────────────────────────────────────────
const DeleteConfirm = ({ agent, onConfirm, onClose, loading }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl">
      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColor(agent.name)} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
        {getInitials(agent.name)}
      </div>
      <div>
        <p className="font-semibold text-slate-900 text-sm">{agent.name}</p>
        <p className="text-xs text-slate-500">{agent.email}</p>
      </div>
    </div>
    <p className="text-sm text-slate-600 leading-relaxed">
      Are you sure you want to delete this agent? This action{" "}
      <span className="font-semibold text-red-600">cannot be undone</span>.
    </p>
    <div className="flex justify-end gap-2">
      <button onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
        Cancel
      </button>
      <button onClick={onConfirm} disabled={loading}
        className="px-5 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-60 rounded-xl transition-colors">
        {loading ? "Deleting..." : "Delete Agent"}
      </button>
    </div>
  </div>
);

// ─── Permissions Modal ────────────────────────────────────────────────────────
const PermissionsModal = ({ agent, onClose, onSavePerms, onSaveFeatures, permLoading, featLoading }) => {
  const { getAllowedUserFeatures } = useAPI();
  const [activeTab, setActiveTab] = useState("permissions");
  const [ownerFeatures, setOwnerFeatures] = useState(null);

  const [perms, setPerms] = useState(
    PERMISSIONS_LIST.reduce((acc, p) => {
      acc[p.key] = agent?.permissions?.[p.key] || false;
      return acc;
    }, {})
  );

  const [features, setFeatures] = useState(
    FEATURES_LIST.reduce((acc, f) => {
      acc[f.key] = agent?.features?.[f.key] || false;
      return acc;
    }, {})
  );

  // Fetch the logged-in owner's features to know which toggles to show
  useEffect(() => {
    getAllowedUserFeatures().then((data) => {
      setOwnerFeatures(data?.features || {});
    });
  }, []);

  const togglePerm = (key) => setPerms((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleFeat = (key) => setFeatures((prev) => ({ ...prev, [key]: !prev[key] }));

  // contacts always shown, broadcast/workflows only if owner has them enabled
  const visibleFeatures = FEATURES_LIST.filter((f) => {
    if (f.key === "contacts") return true;
    if (f.key === "liveChat") return true;
    if (f.key === "broadcasting") return ownerFeatures?.broadcasting === true;
    if (f.key === "workflows") return ownerFeatures?.workflows === true;
    return false;
  });

  const descMap = {
    contacts: "Allow agent to access and manage contacts",
    broadcasting: "Allow agent to send broadcast messages",
    liveChat: "Allow agent to participate in live chat sessions",
    workflows: "Allow agent to view and manage workflows",
  };

  return (
    <Modal open={!!agent} onClose={onClose} title={`Edit Agent — ${agent?.name}`}>
      <div className="space-y-4">

        {/* Tab switcher */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {["permissions", "features"].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-1.5 text-xs font-semibold rounded-lg capitalize transition-all ${activeTab === tab
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
                }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Permissions tab */}
        {activeTab === "permissions" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {PERMISSIONS_LIST.map((p) => (
                <div
                  key={p.key}
                  className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 gap-3"
                >
                  <span className="text-sm font-medium text-slate-700 leading-tight">{p.label}</span>
                  <Toggle checked={perms[p.key]} onChange={() => togglePerm(p.key)} />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={() => onSavePerms(agent.id, perms)}
                disabled={permLoading}
                className="px-5 py-2 text-sm font-semibold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-60 rounded-xl transition-colors"
              >
                {permLoading ? "Saving..." : "Save Permissions"}
              </button>
            </div>
          </div>
        )}

        {/* Features tab */}
        {activeTab === "features" && (
          <div className="space-y-4">
            {ownerFeatures === null ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {visibleFeatures.map((f) => (
                  <div
                    key={f.key}
                    className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 gap-3"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-700">{f.label}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{descMap[f.key]}</p>
                    </div>
                    <Toggle checked={features[f.key]} onChange={() => toggleFeat(f.key)} />
                  </div>
                ))}
                {visibleFeatures.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-6">No features available to assign.</p>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={() => onSaveFeatures(agent.id, features)}
                disabled={featLoading || ownerFeatures === null}
                className="px-5 py-2 text-sm font-semibold text-white bg-blue-500 hover:bg-blue-600 disabled:opacity-60 rounded-xl transition-colors"
              >
                {featLoading ? "Saving..." : "Save Features"}
              </button>
            </div>
          </div>
        )}

      </div>
    </Modal>
  );
};

// ─── Agent Card ───────────────────────────────────────────────────────────────
const AgentCard = ({ agent, onEdit, onDelete, onPermissions }) => {
  const isOwner = agent.role === "Owner";

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:border-slate-300 transition-all duration-200 group relative">

      {/* Actions — only for non-owners, visible on hover */}
      {!isOwner && (
        <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onPermissions(agent)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
            title="Permissions"
          >
            <Settings size={13} />
          </button>
          {/* <button
            onClick={() => onEdit(agent)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
            title="Edit"
          >
            <Pencil size={13} />
          </button> */}
          <button
            onClick={() => onDelete(agent)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
            title="Delete"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}

      {/* Avatar + Name */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${avatarColor(agent.name)} flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm`}>
          {getInitials(agent.name)}
        </div>
        <div className="min-w-0">
          <p className="font-bold text-slate-900 text-sm leading-tight truncate pr-16">{agent.name}</p>
          <p className="text-xs text-slate-400 mt-0.5 truncate">{agent.email}</p>
        </div>
      </div>

      {/* Username + Status */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-1">Username</p>
          <p className="text-xs text-slate-700 font-semibold truncate">{agent.username}</p>
        </div>
        <div>
          {/* <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-1">Status</p>
          <StatusBadge status={agent.status || "Offline"} /> */}
        </div>
      </div>

      {/* Role + Phone */}
      <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
        <RolePill role={agent.role} />
        {agent.phone && (
          <span className="text-[11px] text-slate-400 font-medium tabular-nums">{agent.phone}</span>
        )}
      </div>
    </div>
  );
};

// ─── Main Agents Page ─────────────────────────────────────────────────────────
const Agents = () => {
  const { createAgent, getUsers, updateAgentPermissions, deleteAgent, updateAgentFeatures } = useAPI();

  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [permTarget, setPermTarget] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [permLoading, setPermLoading] = useState(false);
  const [featLoading, setFeatLoading] = useState(false);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const res = await getUsers();
      const formatted = res?.users?.map((user) => ({
        id: user._id,
        name: user.name,
        email: user.email,
        phone: user.phone_number,
        role: user.role,
        username: user.email,
        status: user.accountStatus === "active" ? "Online" : "Offline",
        permissions: user.permissions || {},
        features: user.features || {},   // ← add this
      }));
      setAgents(formatted || []);
    } catch (error) {
      console.error("Failed to fetch agents", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  
    fetchAgents();
  }, [getUsers]);

  const filtered = agents.filter(
    (a) =>
      a.name.toLowerCase().includes(search.toLowerCase()) ||
      a.email.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async (data) => {
    setCreateLoading(true);
    try {
      const payload = {
        name: data.name,
        email: data.email,
        password: data.password,
        phone: data.phone,
        role: data.role.toLowerCase(),
      };
      const res = await createAgent(payload);
      const newAgent = {
        id: res?.id || Date.now().toString(),
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        role: payload.role,
        username: payload.email,
        status: "Offline",
        permissions: {},
      };
      setAgents((prev) => [...prev, newAgent]);
      setCreateOpen(false);


      fetchAgents();
    } catch (error) {
      alert(error?.message || "Failed to create agent");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEdit = async (data) => {
    setEditLoading(true);
    try {
      // const updated = await updateAgent(editTarget.id, data);
      await new Promise((r) => setTimeout(r, 600));
      setAgents((prev) => prev.map((a) => (a.id === editTarget.id ? { ...a, ...data } : a)));
      setEditTarget(null);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteAgent(deleteTarget.id);
      setAgents((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success("Agent deleted successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to delete agent");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSavePermissions = async (agentId, perms) => {
    setPermLoading(true);
    try {
      await updateAgentPermissions(agentId, perms);
      setAgents((prev) =>
        prev.map((a) => (a.id === agentId ? { ...a, permissions: perms } : a))
      );
      setPermTarget(null);
      toast.success("Permissions updated successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to update permissions");
    } finally {
      setPermLoading(false);
    }
  };

  const handleSaveFeatures = async (agentId, features) => {
    setFeatLoading(true);
    try {
      await updateAgentFeatures(agentId, features);
      setAgents((prev) =>
        prev.map((a) => (a.id === agentId ? { ...a, features } : a))
      );
      setPermTarget(null);
      toast.success("Features updated successfully");
    } catch (error) {
      toast.error(error?.message || "Failed to update features");
    } finally {
      setFeatLoading(false);
    }
  };

  const usedPct = Math.min((agents.length / QUOTA) * 100, 100);

  return (
    <div className="w-full">

      {/* ── Top Bar ── */}
      <div className="flex items-center justify-between">
        <p className="font-semibold text-slate-800 text-2xl">Agent management dashboard</p>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Agent Name"
              className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-emerald-300 transition-all w-56 placeholder-slate-400"
            />
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#0d3d36] hover:bg-[#0a2f29] rounded-xl shadow-sm transition-colors"
          >
            <Plus size={15} />
            Add Agent
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="p-6 mt-5 bg-slate-50 rounded-2xl border border-slate-200">

        {/* Quota banner */}
        <div className="mb-5 bg-white border border-slate-200 rounded-2xl px-5 py-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center text-violet-500 flex-shrink-0">
            <Users size={20} />
          </div>
          <div>
            <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-0.5">Agent Quota</p>
            <p className="text-sm font-bold text-slate-900">
              <span className="text-violet-600">{agents.length}</span>
              {/* <span className="text-slate-300 mx-1">/</span> */}
              {/* <span className="text-slate-500">{QUOTA}</span> */}
              {/* <span className="text-slate-400 font-normal ml-2 text-xs">has been used</span> */}
            </p>
          </div>
          {/* <div className="ml-auto flex items-center gap-3">
            <div className="w-36 bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-500"
                style={{ width: `${usedPct}%` }}
              />
            </div>
            <span className="text-xs text-slate-400 font-medium w-8 text-right">{agents.length}/{QUOTA}</span>
          </div> */}
        </div>

        {/* Loading */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-slate-200" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-3.5 bg-slate-200 rounded w-24" />
                    <div className="h-3 bg-slate-100 rounded w-36" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><div className="h-2 bg-slate-100 rounded w-16" /><div className="h-3 bg-slate-200 rounded w-24" /></div>
                  <div className="space-y-1"><div className="h-2 bg-slate-100 rounded w-12" /><div className="h-5 bg-slate-200 rounded-full w-14" /></div>
                </div>
                <div className="pt-3 border-t border-slate-100 flex justify-between">
                  <div className="h-5 bg-slate-200 rounded-full w-14" />
                  <div className="h-3 bg-slate-100 rounded w-24" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-28 text-slate-300">
            <Users size={44} className="mb-3" />
            <p className="text-sm font-semibold text-slate-400">No agents found</p>
            <p className="text-xs text-slate-400 mt-1">Try a different name or add a new agent</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                onEdit={setEditTarget}
                onDelete={setDeleteTarget}
                onPermissions={setPermTarget}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create Agent">
        <AgentForm
          onSubmit={handleCreate}
          onCancel={() => setCreateOpen(false)}
          submitLabel="Create Agent"
          loading={createLoading}
        />
      </Modal>

      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title="Edit Agent">
        {editTarget && (
          <AgentForm
            initial={editTarget}
            onSubmit={handleEdit}
            onCancel={() => setEditTarget(null)}
            submitLabel="Save Changes"
            loading={editLoading}
          />
        )}
      </Modal>

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Agent">
        {deleteTarget && (
          <DeleteConfirm
            agent={deleteTarget}
            onConfirm={handleDelete}
            onClose={() => setDeleteTarget(null)}
            loading={deleteLoading}
          />
        )}
      </Modal>

      {permTarget && (
        <PermissionsModal
          agent={permTarget}
          onClose={() => setPermTarget(null)}
          onSavePerms={handleSavePermissions}
          onSaveFeatures={handleSaveFeatures}
          permLoading={permLoading}
          featLoading={featLoading}
        />
      )}
    </div>
  );
};

export default Agents;  